import abcLogo from "/abclabs-white.svg";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PreJoin } from "@livekit/components-react";
import "@livekit/components-styles";
import "./App.css";
import { ErrorDialog } from "./components/ErrorDialog";
import {
  ExternalE2EEKeyProvider,
  Room,
  RoomEvent,
  type E2EEOptions,
} from "livekit-client";
import { LanguageButton } from "./components/LanguageButton.tsx";
import { useTranslation } from "react-i18next";
import { WaitRoom, type AdmittanceProps } from "./components/WaitRoom.tsx";
import LandingPage from "./components/LandingPage.tsx";
import { InCallContainer } from "./components/InCallContainter.tsx";

const LIVEKIT_SERVER_URL = import.meta.env.VITE_LIVEKIT_SERVER_URL as string;
if (!LIVEKIT_SERVER_URL) throw new Error("VITE_LIVEKIT_SERVER_URL is required");

const CAREOS_CONNECT_API_URL = import.meta.env
  .VITE_CAREOS_CONNECT_API_URL as string;
if (!CAREOS_CONNECT_API_URL)
  throw new Error("VITE_CAREOS_CONNECT_API_URL is required");

type SessionState =
  | "landingPage"
  | "prejoin"
  | "joining"
  | "waitingRoom"
  | "active"
  | "error";

type PrejoinValues = {
  username?: string;
  audioEnabled?: boolean;
  videoEnabled?: boolean;
  audioDeviceId?: string | null;
  videoDeviceId?: string | null;
  captureSettings?: {
    resolution?: { width: number; height: number; frameRate?: number };
  };
};

export default function App() {
  const [urlSid, setUrlSid] = useState<string | null>(null);
  const [prejoinValues, setPrejoinValues] = useState<PrejoinValues | null>(
    null,
  );
  const [isWaitingAfterPrejoin, setIsWaitingAfterPrejoin] = useState(false);

  useEffect(() => {
    const readHash = () => {
      const params = new URLSearchParams(window.location.hash.slice(1));
      const sidRaw = params.get("sid");

      const sid = sidRaw && sidRaw.trim() ? sidRaw.trim() : null;

      setUrlSid(sid);

      if (sid) {
        setSessionState("prejoin");
      } else {
        setSessionState("landingPage");
      }
    };

    readHash();
    window.addEventListener("hashchange", readHash);
    return () => window.removeEventListener("hashchange", readHash);
  }, []);
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [sessionState, setSessionState] = useState<SessionState>("prejoin");

  const [remoteSigningLink, setRemoteSigningLink] = useState<string | null>(
    null,
  );

  const [lkToken, setLkToken] = useState<string | null>(null);

  const [showDisconnectCard, setShowDisconnectCard] = useState(false);
  const [showSignedNotification, setShowSignedNotification] = useState(false);

  const showDisconnectedCard = useCallback(() => {
    setShowDisconnectCard(true);
    setTimeout(() => {
      setShowDisconnectCard(false);
    }, 6_000);
  }, []);

  const keyProviderRef = useRef<ExternalE2EEKeyProvider | null>(null);
  if (!keyProviderRef.current) {
    keyProviderRef.current = new ExternalE2EEKeyProvider();
  }

  const room = useMemo(() => {
    const keyProvider = keyProviderRef.current!;
    const e2ee: E2EEOptions = {
      keyProvider,
      worker: new Worker(
        new URL("livekit-client/e2ee-worker", import.meta.url),
      ),
    };

    const roomOptions = {
      e2ee,
    };
    return new Room(roomOptions);
  }, []);

  useEffect(() => {
    return () => {
      room.disconnect();
    };
  }, [room]);

  const [connected, setConnected] = useState(false);

  const handlePrejoinSubmit = useCallback((values: PrejoinValues) => {
    // Save user’s media choices for later (when we actually connect)
    setPrejoinValues(values);

    // Stay in the prejoin session state, but move into "waiting" mode
    setIsWaitingAfterPrejoin(true);

    // Optionally: you could also do any client-side validation here.
  }, []);

  const handleAdmitted = useCallback(
    async (admittance: AdmittanceProps) => {
      const { token, encryptionKey } = admittance;

      try {
        setSessionState("joining");

        const keyProvider = keyProviderRef.current;
        if (!keyProvider) {
          throw new Error("E2EE key provider not initialized");
        }

        await keyProvider.setKey(encryptionKey);
        await room.setE2EEEnabled(true);

        await room.connect(LIVEKIT_SERVER_URL, token, {
          autoSubscribe: true,
        });

        const values = prejoinValues ?? {};

        if (values.audioEnabled) {
          await room.localParticipant.setMicrophoneEnabled(true, {
            deviceId: values.audioDeviceId ?? "default",
          });
        } else {
          await room.localParticipant.setMicrophoneEnabled(false, {
            deviceId: values.audioDeviceId ?? "default",
          });
        }

        if (values.videoEnabled) {
          await room.localParticipant.setCameraEnabled(true, {
            deviceId: values.videoDeviceId ?? "default",
          });
        } else {
          await room.localParticipant.setCameraEnabled(false);
        }

        setLkToken(token);
        setConnected(true);
        setSessionState("active");
      } catch (e) {
        console.error("Failed to connect after admission:", e);
        setErrorMessage(t("errors.tryAgainLater"));
        setSessionState("error");
      }
    },
    [room, prejoinValues, t],
  );

  useEffect(() => {
    const onDisconnected = () => {
      setLkToken(null);
      setRemoteSigningLink(null);
      setIsWaitingAfterPrejoin(false);
      setSessionState("prejoin");
    };

    room.off(RoomEvent.Disconnected, onDisconnected);
    room.on(RoomEvent.Disconnected, onDisconnected);

    return () => {
      room.off(RoomEvent.Disconnected, onDisconnected);
    };
  }, [room, urlSid]);

  const handleDismissed = useCallback(async () => {
    setLkToken(null);
    setRemoteSigningLink(null);
    setIsWaitingAfterPrejoin(false);
    setSessionState("prejoin");
  }, []);

  const handleWaitRoomError = useCallback(async (message: string) => {
    setErrorMessage(message);
    setLkToken(null);
    setRemoteSigningLink(null);
    setIsWaitingAfterPrejoin(false);
    setSessionState("prejoin");
  }, []);

  useEffect(() => {
    const onConnected = () => {
      setSessionState("active");
    };

    room.off(RoomEvent.Connected, onConnected);
    room.on(RoomEvent.Connected, onConnected);

    return () => {
      room.off(RoomEvent.Disconnected, onConnected);
    };
  }, [room, urlSid]);

  useEffect(() => {
    const onParticipantDisconnected = () => {
      const remoteParticipants = room.remoteParticipants;
      let numCollectors = 0;
      for (const participant of remoteParticipants.values()) {
        if (participant.identity.startsWith("ABC_LABS_COLLECTOR")) {
          numCollectors++;
        }
      }
      if (numCollectors === 0) {
        showDisconnectedCard();
        setLkToken(null);
        setIsWaitingAfterPrejoin(false);
        setSessionState("prejoin");
        room.disconnect();
      }
    };

    room.off(RoomEvent.ParticipantDisconnected, onParticipantDisconnected);
    room.on(RoomEvent.ParticipantDisconnected, onParticipantDisconnected);

    return () => {
      room.off(RoomEvent.ParticipantDisconnected, onParticipantDisconnected);
    };
  }, [room, showDisconnectedCard, urlSid]);

  useEffect(() => {
    return () => {
      room.disconnect();
    };
  }, [room]);

  useEffect(() => {
    // listen for sign success from postmessage from iframe child
    const handlePostMessage = (event: MessageEvent) => {
      if (event.data.type === "DONOR_SIGN_SUCCESS" && remoteSigningLink) {
        setRemoteSigningLink(null);
        setShowSignedNotification(true);
        setTimeout(() => {
          setShowSignedNotification(false);
        }, 6_000);
      }
    };
    window.addEventListener("message", handlePostMessage);
    return () => window.removeEventListener("message", handlePostMessage);
  }, [remoteSigningLink]);

  useEffect(() => {
    if (!connected) return;

    const remoteSigningDataHandler = async ({
      payload,
    }: {
      payload?: string;
    }) => {
      try {
        const data = payload
          ? (JSON.parse(payload) as {
              signingLink: string;
            })
          : null;
        if (data?.signingLink) {
          setRemoteSigningLink(data.signingLink);
        }
      } catch {
        // ignore invalid payload
      }
      return "ok";
    };

    room.registerRpcMethod("remoteSigningData", remoteSigningDataHandler);
    return () => {
      try {
        room.unregisterRpcMethod?.("remoteSigningData");
        // eslint-disable-next-line no-empty
      } catch {}
    };
  }, [connected, room]);

  useEffect(() => {
    if (!connected) return;

    const handler = async () => {
      showDisconnectedCard();
      room.disconnect();
      return "ok"; // reply so caller doesn't timeout
    };

    try {
      room.unregisterRpcMethod?.("disconnect");
    } catch {
      // Nothing to do here
    }

    room.registerRpcMethod("disconnect", handler);

    return () => {
      try {
        room.unregisterRpcMethod?.("disconnect");
      } catch {
        // Nothing to do here
      }
    };
  }, [connected, room, showDisconnectedCard]);

  let lkTheme = "default";
  const params = new URLSearchParams(window.location.search);
  const dark = params.has("dark");
  if (dark) {
    lkTheme = "abclabs-dark";
  }

  return (
    <main className={`relative bg-black`} data-lk-theme="default">
      <div
        className="mx-auto w-full min-h-screen flex flex-col"
        data-lk-theme={lkTheme}
      >
        {sessionState !== "active" && (
          <header
            id="page-header"
            className="relative flex items-end justify-center mb-2 h-20 px-4"
          >
            {/* Left-aligned logo */}
            <img
              src={abcLogo}
              alt="Abc Labs Logo"
              className="h-20 w-20 absolute left-4 bottom-0"
            />

            {/* Centered title */}
            <h1 className="hidden md:inline text-5xl font-light text-white -translate-y-2">
              {t("header.title")}
            </h1>

            <LanguageButton />
          </header>
        )}
        {sessionState === "landingPage" && (
          <LandingPage
            buttonLabel={t("landingPage.buttonLabel", "OK")}
            label={t("landingPage.label", "Enter session ID")}
          />
        )}
        {sessionState === "prejoin" && (
          <div className="grid place-items-center px-4 pb-10 h-full">
            <div
              className={
                "relative w-full h-[calc(100vh-150px)]" +
                (isWaitingAfterPrejoin ? " prejoin-waiting" : "")
              }
            >
              <PreJoin
                onSubmit={handlePrejoinSubmit}
                defaults={{
                  audioEnabled: true,
                  videoEnabled: true,
                  username: "donor",
                }}
                joinLabel={t("join.joinSession")}
                micLabel={t("controls.microphone")}
                camLabel={t("controls.camera")}
              />

              {isWaitingAfterPrejoin && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="pointer-events-auto">
                    <WaitRoom
                      sessionId={urlSid ?? ""}
                      apiBaseUrl={CAREOS_CONNECT_API_URL}
                      onAdmitted={handleAdmitted}
                      onDismissed={handleDismissed}
                      onError={handleWaitRoomError}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {sessionState === "joining" && (
          <p className="text-center text-white font-thin text-sm mb-6">
            {t("join.connecting")}
          </p>
        )}
        {sessionState === "active" && connected && lkToken && (
          <InCallContainer
            room={room}
            remoteSigningLink={remoteSigningLink}
            showSignedNotification={showSignedNotification}
          />
        )}
        <ErrorDialog
          message={errorMessage}
          onClose={useCallback(() => setErrorMessage(""), [])}
        />

        {showDisconnectCard && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/60" />

            <div className="relative z-10">
              <div className="card text-center px-8 py-6 rounded-2xl shadow-xl bg-blue-800/85 text-white disconnect-fade">
                <p>
                  {t(
                    "status.disconnectedFromSession",
                    "You have been disconnected from the session",
                  )}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
